import { useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Select,
  Button,
  Group,
  Alert,
  Paper,
  Checkbox,
} from '@mantine/core';
import {
  IconDownload,
  IconAlertTriangle,
  IconDeviceDesktop,
  IconPhoto,
} from '@tabler/icons-react';
import { useDeployImage, type FogHost, type FogImage } from '@/hooks';

interface DeployImageModalProps {
  opened: boolean;
  onClose: () => void;
  host: FogHost | null;
  images: FogImage[];
}

export function DeployImageModal({ opened, onClose, host, images }: DeployImageModalProps) {
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const deployMutation = useDeployImage();

  const handleClose = () => {
    setSelectedImageId(null);
    setConfirmed(false);
    onClose();
  };

  const handleDeploy = async () => {
    if (!host || !selectedImageId) return;

    try {
      await deployMutation.mutateAsync({
        hostId: host.id,
        imageId: parseInt(selectedImageId, 10),
      });
      handleClose();
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const selectedImage = images.find((img) => img.id.toString() === selectedImageId);

  const imageOptions = images
    .filter((img) => img.isEnabled !== false)
    .map((img) => ({
      value: img.id.toString(),
      label: `${img.name}${img.osName ? ` (${img.osName})` : ''}`,
    }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Deploy Image"
      size="md"
    >
      <Stack gap="md">
        {/* Host Info */}
        <Paper withBorder p="sm" radius="md" bg="gray.0">
          <Group gap="xs">
            <IconDeviceDesktop size={18} />
            <Text fw={500}>Target Host:</Text>
            <Text>{host?.name}</Text>
          </Group>
          <Text size="sm" c="dimmed" mt={4}>
            MAC: {host?.mac || host?.macs?.[0] || 'Unknown'}
          </Text>
        </Paper>

        {/* Warning */}
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="orange"
          variant="light"
        >
          <Text size="sm">
            Deploying an image will <strong>overwrite all data</strong> on the target machine.
            Make sure the host is ready to be reimaged.
          </Text>
        </Alert>

        {/* Image Selection */}
        <Select
          label="Select Image"
          placeholder="Choose an image to deploy"
          data={imageOptions}
          value={selectedImageId}
          onChange={setSelectedImageId}
          searchable
          leftSection={<IconPhoto size={16} />}
        />

        {/* Selected Image Info */}
        {selectedImage && (
          <Paper withBorder p="sm" radius="md">
            <Stack gap={4}>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Image Name</Text>
                <Text size="sm" fw={500}>{selectedImage.name}</Text>
              </Group>
              {selectedImage.osName && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Operating System</Text>
                  <Text size="sm">{selectedImage.osName}</Text>
                </Group>
              )}
              {selectedImage.size && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Size</Text>
                  <Text size="sm">{selectedImage.size}</Text>
                </Group>
              )}
              {selectedImage.deployed && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Last Deployed</Text>
                  <Text size="sm">{new Date(selectedImage.deployed).toLocaleDateString()}</Text>
                </Group>
              )}
            </Stack>
          </Paper>
        )}

        {/* Confirmation Checkbox */}
        <Checkbox
          label="I understand this will erase all data on the target machine"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.currentTarget.checked)}
        />

        {/* Actions */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handleDeploy}
            loading={deployMutation.isPending}
            disabled={!selectedImageId || !confirmed}
            color="blue"
          >
            Deploy Image
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
